import { useEffect } from "react";
import {
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { useAvailableTeleconsultoria } from "../hooks/useAvailableTeleconsultoria";
import { useTeleconsultoriaPresence } from "../presence/useTeleconsultoriaPresence";
import type { TeleconsultoriaPresenceStatus } from "../presence/probeTeleconsultoriaPeer";
import { UNKNOWN_ORGANIZATION } from "../services/resolveOrganizationDisplays";
import type { TeleconsultoriaGuest } from "../types";

/**
 * O mesmo profissional pode ter uma agenda por organização.
 * O HCS é por organização, então practitioner + HCS identifica a linha.
 */
function isSameGuest(
  a: TeleconsultoriaGuest | null,
  b: TeleconsultoriaGuest,
): boolean {
  return (
    a?.practitioner.reference === b.practitioner.reference &&
    a?.healthcareService?.reference === b.healthcareService?.reference
  );
}

function PresenceLabel({ status }: { status: TeleconsultoriaPresenceStatus }) {
  if (status === "online") {
    return <Chip size="small" label="Online" color="success" />;
  }
  if (status === "offline") {
    return <Chip size="small" label="Offline" />;
  }
  if (status === "unknown") {
    return <Chip size="small" label="Desconhecido" variant="outlined" />;
  }
  return <Chip size="small" label="Verificando" variant="outlined" />;
}

interface TeleconsultoriaProfessionalPickerProps {
  selected: TeleconsultoriaGuest | null;
  onSelect: (guest: TeleconsultoriaGuest | null) => void;
}

export function TeleconsultoriaProfessionalPicker({
  selected,
  onSelect,
}: TeleconsultoriaProfessionalPickerProps) {
  const { rows, loading, loaded, organizationDisplays, reload } =
    useAvailableTeleconsultoria();
  // Presença é por pessoa: sem duplicatas quando o profissional está em várias organizações.
  const presence = useTeleconsultoriaPresence([
    ...new Set(rows.map((row) => row.guest.practitioner.reference)),
  ]);

  // Depois de recarregar: quem saiu da lista não pode continuar selecionado.
  useEffect(() => {
    if (!loaded || !selected) return;
    if (!rows.some((row) => isSameGuest(selected, row.guest))) onSelect(null);
  }, [loaded, rows, selected, onSelect]);

  // Visível em todos os estados (loading, vazio e lista).
  const toolbar = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ mb: 1 }}
    >
      <Typography variant="subtitle2" color="text.secondary">
        Profissionais disponíveis
      </Typography>
      <Tooltip title="Atualizar lista">
        <span>
          <IconButton
            size="small"
            onClick={reload}
            disabled={loading}
            aria-label="Atualizar lista"
          >
            <Refresh fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  if (loading) {
    return (
      <>
        {toolbar}
        <CircularProgress size={28} />
      </>
    );
  }

  if (!rows.length) {
    return (
      <>
        {toolbar}
        <Typography variant="body2" color="text.secondary">
          Nenhum profissional disponível para teleconsultoria agora.
        </Typography>
      </>
    );
  }

  return (
    <>
      {toolbar}
      <List disablePadding>
        {rows.map((row) => {
          const selectedRow = isSameGuest(selected, row.guest);
          // Organização primeiro: é o que diferencia linhas do mesmo profissional.
          const organizationLabel =
            row.organizationDisplay ||
            (row.organizationId
              ? organizationDisplays.get(row.organizationId)
              : UNKNOWN_ORGANIZATION);
          const secondary = [
            organizationLabel,
            row.guest.healthcareService?.display,
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <ListItemButton
              key={row.scheduleId}
              selected={selectedRow}
              onClick={() => onSelect(selectedRow ? null : row.guest)}
            >
              <ListItemText
                disableTypography
                primary={
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Typography variant="body1" component="span">
                      {row.guest.practitioner.display || "Profissional"}
                    </Typography>
                    <PresenceLabel
                      status={
                        presence[row.guest.practitioner.reference] ??
                        "checking"
                      }
                    />
                  </Stack>
                }
                secondary={
                  secondary ? (
                    <Typography variant="body2" color="text.secondary">
                      {secondary}
                    </Typography>
                  ) : undefined
                }
              />
            </ListItemButton>
          );
        })}
      </List>
    </>
  );
}
